import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ChecklistIcon from '@mui/icons-material/Checklist'
import api from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import { getActiveLayout } from '../../utils/layoutUtils'
import { AdminSectionCard } from './AdminConsoleShell'
import OperationalKpiCard from './OperationalKpiCard'
import OperationalAlertList from './OperationalAlertList'
import ProductOperationalStatusTable from './ProductOperationalStatusTable'

const normalizeCatalogCount = (data) => {
  if (Array.isArray(data)) return data.length
  if (Array.isArray(data?.data)) return data.data.length
  if (Array.isArray(data?.items)) return data.items.length
  return 0
}

const getProductName = (product = {}) =>
  product?.name_product || product?.name || `Producto ${product?.id || 'N/A'}`

const getClientName = (product = {}) =>
  product?.client?.name_client ||
  product?.name_client ||
  product?.clientName ||
  (product?.client_id ? `Cliente ${product.client_id}` : 'Sin cliente visible')

const mapProductStatus = (product, layouts) => {
  const layoutsList = Array.isArray(layouts) ? layouts : []
  const activeLayout = getActiveLayout(layoutsList)
  const layoutsCount = layoutsList.length
  const hasLayouts = layoutsCount > 0
  const hasActiveLayout = Boolean(
    activeLayout?.active === true ||
      activeLayout?.is_active === true ||
      activeLayout?.layout_product?.active === true ||
      activeLayout?.LayoutProduct?.active === true ||
      (layoutsCount === 1 && activeLayout)
  )

  let status = 'healthy'
  let recommendedAction = 'Sin acción inmediata.'

  if (!hasLayouts) {
    status = 'critical'
    recommendedAction = 'Crear o asignar un layout operativo.'
  } else if (!hasActiveLayout) {
    status = 'warning'
    recommendedAction = 'Validar cuál layout debe quedar activo.'
  } else if (layoutsCount > 1) {
    status = 'warning'
    recommendedAction = 'Revisar duplicidad y confirmar layout principal.'
  }

  return {
    productId: product?.id,
    productName: getProductName(product),
    clientName: getClientName(product),
    layoutsCount,
    hasLayouts,
    hasActiveLayout,
    status,
    recommendedAction,
  }
}

const buildAlerts = ({
  productStatuses,
  catalogs,
  totalLayoutsDetected,
  totalProducts,
}) => {
  const alerts = []
  const productsWithoutLayouts = productStatuses.filter((item) => !item.hasLayouts)
  const productsWithoutActiveLayout = productStatuses.filter(
    (item) => item.hasLayouts && !item.hasActiveLayout
  )

  if (totalProducts === 0) {
    alerts.push({
      severity: 'warning',
      title: 'Sin productos visibles',
      description:
        'No se detectaron productos en el contexto actual. Verifica permisos o contexto operativo.',
    })
  }

  if (productsWithoutLayouts.length > 0) {
    alerts.push({
      severity: 'error',
      title: 'Productos sin layout',
      description: `${productsWithoutLayouts.length} producto(s) requieren asignación de layout para operar con el motor actual.`,
    })
  }

  if (productsWithoutActiveLayout.length > 0) {
    alerts.push({
      severity: 'warning',
      title: 'Layouts sin activación clara',
      description: `${productsWithoutActiveLayout.length} producto(s) tienen layouts detectados, pero no un layout activo inequívoco.`,
    })
  }

  if (totalLayoutsDetected === 0 && totalProducts > 0) {
    alerts.push({
      severity: 'error',
      title: 'Motor sin layouts aplicables',
      description:
        'Hay productos disponibles, pero no se detectaron layouts en el dominio actual.',
    })
  }

  ;['actions', 'contacts', 'results'].forEach((catalogKey) => {
    if (!catalogs[catalogKey].available) {
      alerts.push({
        severity: 'warning',
        title: `Catálogo no disponible: ${catalogs[catalogKey].label}`,
        description: catalogs[catalogKey].message,
      })
    }
  })

  if (alerts.length === 0) {
    alerts.push({
      severity: 'success',
      title: 'Sin alertas operativas críticas',
      description:
        'El dominio presenta cobertura de layouts y catálogos accesibles con la información disponible.',
    })
  }

  return alerts
}

export default function OperationalInsightsPanel({ products = [], onNavigateProduct }) {
  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products.filter(Boolean) : []),
    [products]
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [productStatuses, setProductStatuses] = useState([])
  const [catalogs, setCatalogs] = useState({
    actions: { label: 'Acciones', count: 0, available: false, message: '' },
    contacts: { label: 'Contactos', count: 0, available: false, message: '' },
    results: { label: 'Resultados', count: 0, available: false, message: '' },
  })

  useEffect(() => {
    let cancelled = false

    const loadOperationalData = async () => {
      setLoading(true)
      setError('')

      const catalogRequests = [
        { key: 'actions', label: 'Acciones', endpoint: '/codes/actions' },
        { key: 'contacts', label: 'Contactos', endpoint: '/codes/contacts' },
        { key: 'results', label: 'Resultados', endpoint: '/codes/results' },
      ]

      try {
        const [catalogResults, layoutsResults] = await Promise.all([
          Promise.allSettled(
            catalogRequests.map(({ endpoint }) => api.get(endpoint))
          ),
          Promise.allSettled(
            safeProducts.map((product) => getLayoutsByProduct(product?.id))
          ),
        ])

        if (cancelled) return

        const nextCatalogs = catalogRequests.reduce((acc, item, index) => {
          const result = catalogResults[index]
          if (result.status === 'fulfilled') {
            acc[item.key] = {
              label: item.label,
              count: normalizeCatalogCount(result.value?.data),
              available: true,
              message: 'Disponible',
            }
          } else {
            acc[item.key] = {
              label: item.label,
              count: 0,
              available: false,
              message: 'No fue posible consultar el catálogo con el endpoint existente.',
            }
          }
          return acc
        }, {})

        const nextStatuses = safeProducts.map((product, index) => {
          const result = layoutsResults[index]
          const rawPayload = result?.status === 'fulfilled' ? result.value : []
          const payload = rawPayload?.data ?? rawPayload
          const layouts = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.layouts)
              ? payload.layouts
              : payload
                ? [payload]
                : []

          return mapProductStatus(product, layouts)
        })

        setCatalogs(nextCatalogs)
        setProductStatuses(nextStatuses)
      } catch (err) {
        console.error('[OperationalInsightsPanel] Error loading operational data:', err)
        if (!cancelled) {
          setCatalogs({
            actions: { label: 'Acciones', count: 0, available: false, message: 'No disponible' },
            contacts: { label: 'Contactos', count: 0, available: false, message: 'No disponible' },
            results: { label: 'Resultados', count: 0, available: false, message: 'No disponible' },
          })
          setProductStatuses(safeProducts.map((product) => mapProductStatus(product, [])))
          setError('No fue posible construir el panel operativo con la información actual.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadOperationalData()

    return () => {
      cancelled = true
    }
  }, [safeProducts])

  const kpis = useMemo(() => {
    const totalProducts = safeProducts.length
    const productsWithLayouts = productStatuses.filter((item) => item.hasLayouts).length
    const productsWithoutLayouts = totalProducts - productsWithLayouts
    const totalLayoutsDetected = productStatuses.reduce(
      (sum, item) => sum + item.layoutsCount,
      0
    )

    return {
      totalProducts,
      productsWithLayouts,
      productsWithoutLayouts,
      totalLayoutsDetected,
      actionCodesCount: catalogs.actions?.count || 0,
      contactCodesCount: catalogs.contacts?.count || 0,
      resultCodesCount: catalogs.results?.count || 0,
    }
  }, [catalogs, productStatuses, safeProducts.length])

  const alerts = useMemo(
    () =>
      buildAlerts({
        productStatuses,
        catalogs,
        totalLayoutsDetected: kpis.totalLayoutsDetected,
        totalProducts: kpis.totalProducts,
      }),
    [catalogs, kpis.totalLayoutsDetected, kpis.totalProducts, productStatuses]
  )

  return (
    <AdminSectionCard
      title="Operational Insights Panel"
      subtitle="Visibilidad operacional de productos, layouts y catálogos usando únicamente fuentes ya disponibles en el sistema."
      actions={<DashboardCustomizeIcon color="action" />}
    >
      <Stack spacing={3}>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={22} />
            <Typography variant="body2">
              Consolidando métricas operativas y disponibilidad de catálogos…
            </Typography>
          </Stack>
        )}

        {!loading && error && <Alert severity="warning">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <OperationalKpiCard
              title="Productos"
              value={kpis.totalProducts}
              subtitle={`${kpis.productsWithLayouts} con layout / ${kpis.productsWithoutLayouts} pendientes`}
              status={kpis.productsWithoutLayouts > 0 ? 'warning' : 'healthy'}
              icon={<Inventory2Icon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OperationalKpiCard
              title="Layouts detectados"
              value={kpis.totalLayoutsDetected}
              subtitle="Total agregado por producto en contexto"
              status={kpis.totalLayoutsDetected > 0 ? 'healthy' : 'critical'}
              icon={<ViewQuiltIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OperationalKpiCard
              title="Catálogos disponibles"
              value={`${[catalogs.actions, catalogs.contacts, catalogs.results].filter((item) => item?.available).length}/3`}
              subtitle={`A:${kpis.actionCodesCount} · C:${kpis.contactCodesCount} · R:${kpis.resultCodesCount}`}
              status={alerts.some((item) => item.severity === 'warning') ? 'warning' : 'healthy'}
              icon={<ChecklistIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OperationalKpiCard
              title="Alertas operativas"
              value={alerts.filter((item) => item.severity !== 'success').length}
              subtitle="Prioridad según cobertura de layouts y catálogos"
              status={alerts.some((item) => item.severity === 'error') ? 'critical' : alerts.some((item) => item.severity === 'warning') ? 'warning' : 'healthy'}
              icon={<WarningAmberIcon fontSize="small" />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          <Grid item xs={12} xl={4}>
            <Box sx={{ height: '100%' }}>
              <OperationalAlertList alerts={alerts} />
            </Box>
          </Grid>
          <Grid item xs={12} xl={8}>
            <ProductOperationalStatusTable
              rows={productStatuses}
              onNavigateProduct={onNavigateProduct}
            />
          </Grid>
        </Grid>
      </Stack>
    </AdminSectionCard>
  )
}

OperationalInsightsPanel.propTypes = {
  products: PropTypes.array,
  onNavigateProduct: PropTypes.func,
}
